import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import type { Shipment } from "@/lib/supabase";

interface ShipmentStats {
  totalShipments: number;
  activeShipments: number;
  totalRevenue: number;
}

interface ShipmentContextValue {
  stats: ShipmentStats;
  recentShipments: Shipment[];
  loadingStats: boolean;
  loadingRecent: boolean;
  refreshStats: () => Promise<void>;
  fetchRecent: (limit?: number) => Promise<void>;
}

const ShipmentContext = createContext<ShipmentContextValue | undefined>(undefined);

export function ShipmentProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<ShipmentStats>({
    totalShipments: 0,
    activeShipments: 0,
    totalRevenue: 0,
  });
  const [recentShipments, setRecentShipments] = useState<Shipment[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const refreshStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data: totalShipments, error: totalError } = await supabase
        .from("shipments")
        .select("id", { count: "exact" });

      const { data: activeShipments, error: activeError } = await supabase
        .from("shipments")
        .select("id", { count: "exact" })
        .in("status", [
          "created",
          "estimated",
          "confirmed",
          "picking_up_from_sender",
          "received_at_warehouse",
          "processing_at_warehouse",
          "in_transit_to_country",
          "arrived_at_warehouse",
          "ready_for_pickup",
          "in_transit_to_recipient",
        ]);

      const { data: revenueData, error: revenueError } = await supabase
        .from("shipments")
        .select("total_cost");

      if (totalError || activeError || revenueError) {
        throw totalError || activeError || revenueError;
      }

      const totalRevenue =
        revenueData?.reduce(
          (sum, shipment) => sum + Number(shipment.total_cost),
          0
        ) || 0;

      setStats({
        totalShipments: totalShipments?.length || 0,
        activeShipments: activeShipments?.length || 0,
        totalRevenue,
      });
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchRecent = useCallback(async (limit = 10) => {
    setLoadingRecent(true);
    try {
      const { data, error } = await supabase
        .from("shipments")
        .select(
          `
          *,
          customer:customers(*),
          packages:shipment_packages(
            *,
            items:package_items(*)
          ),
          tracking_events:tracking_events(*)
        `
        )
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      setRecentShipments(data || []);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  return (
    <ShipmentContext.Provider
      value={{
        stats,
        recentShipments,
        loadingStats,
        loadingRecent,
        refreshStats,
        fetchRecent,
      }}
    >
      {children}
    </ShipmentContext.Provider>
  );
}

export function useShipment() {
  const context = useContext(ShipmentContext);
  if (!context) {
    throw new Error("useShipment must be used within a ShipmentProvider");
  }
  return context;
}
